/**
 * Formats a date value from various sources (Firestore Timestamp, Date object, ISO string, etc.)
 * into a human-readable date string.
 *
 * @param dateValue - The date value to format. Can be:
 *   - Firestore Timestamp (with _seconds/_nanoseconds or seconds/nanoseconds)
 *   - Firestore Timestamp object (with toDate method)
 *   - ISO date string
 *   - JavaScript Date object
 *   - null or undefined
 * @param defaultValue - The value to return if date is invalid or missing. Defaults to empty string.
 * @returns Formatted date string in format "MMM DD, YYYY" (e.g., "Jan 15, 2024")
 */
export const formatDate = (dateValue: any, defaultValue = ''): string => {
  if (!dateValue) return defaultValue;

  // Handle serialized Firestore Timestamp object (with _seconds and _nanoseconds)
  if (dateValue && typeof dateValue === 'object') {
    // Check for _seconds (serialized Firestore Timestamp)
    if (dateValue._seconds !== undefined) {
      const seconds = dateValue._seconds || 0;
      const nanoseconds = dateValue._nanoseconds || 0;
      const milliseconds = seconds * 1000 + nanoseconds / 1000000;
      const dateObj = new Date(milliseconds);
      if (isNaN(dateObj.getTime())) {
        return defaultValue;
      }
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    // Check for seconds (without underscore)
    if (dateValue.seconds !== undefined) {
      const seconds = dateValue.seconds || 0;
      const nanoseconds = dateValue.nanoseconds || 0;
      const milliseconds = seconds * 1000 + nanoseconds / 1000000;
      const dateObj = new Date(milliseconds);
      if (isNaN(dateObj.getTime())) {
        return defaultValue;
      }
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    // Check if it has toDate method (Firestore Timestamp)
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      try {
        const firestoreDate = dateValue.toDate();
        if (isNaN(firestoreDate.getTime())) {
          return defaultValue;
        }
        return firestoreDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return defaultValue;
      }
    }
  }

  // Handle ISO string
  if (typeof dateValue === 'string') {
    const parsedDate = new Date(dateValue);
    if (isNaN(parsedDate.getTime())) {
      return defaultValue;
    }
    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Handle Date objects
  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) {
      return defaultValue;
    }
    return dateValue.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  return defaultValue;
};
